import React, { useState } from "react";
import PropTypes from "prop-types";
import reactStringReplace from "react-string-replace";
import { PageLayout } from "../../../../components";
import { FilePicker } from "../../components";
import {
  allRequiredPhotosUploaded,
  formatImagesForPost,
  getIsInValid,
  tryAgain
} from "../../helpers";
import { useSelector, useDispatch } from "react-redux";
import {
  getSourceSystemIsCompactView,
  selectImageUploadData
} from "../../../../store/selectors";
import { imageUploadSlice } from "../../../../store/slices/ImageUploads";
import { useNavigate } from "react-router-dom";
import { MobilePhotoActions } from "../../../../components/MobilePhotoActions/MobilePhotoActions";
import { MobilePhotoAction } from "../../../../data/MobilePhotoAction";
import { sendEvent } from "../../../../helpers/analytics";

export default function Helper({ currentHelper, handleChange }) {
  const {
    maxUploads,
    images,
    requiredPhotoData,
    mobileDeviceWithCamera,
    mobilePhotoUploadChoiceMade
  } = useSelector(selectImageUploadData);
  const {
    label,
    helperText,
    boldLabelText,
    boldHeaderText,
    imageQuantityRequired,
    boldHeaderTextAdditional,
    image,
    showPreviewImage
  } = currentHelper;
  const [validation, setValidation] = useState(null);
  const dispatch = useDispatch();
  const isCompactView = useSelector(getSourceSystemIsCompactView);

  const helperTextBelowImage = reactStringReplace(
    helperText,
    boldLabelText,
    (match, i) => (
      <span key={`${match}-${i}`}>
        <b>{match}</b>
      </span>
    )
  );

  const hasAllRequiredImagesUploaded = allRequiredPhotosUploaded(
    images,
    requiredPhotoData
  );

  const navigate = useNavigate();

  const labelWithFirstInstanceBoldenedWords = reactStringReplace(
    label,
    boldHeaderText,
    (match, i) => (
      <span key={`${match}-${i}`}>
        <b>{match}</b>
      </span>
    )
  );

  const helperTextAboveImage = reactStringReplace(
    labelWithFirstInstanceBoldenedWords,
    boldHeaderTextAdditional,
    (match, i) => {
      if (!match) return;
      return (
        <span key={`${match}-${i}`}>
          <b>{match}</b>
        </span>
      );
    }
  );

  const onChange = (imageFiles, mobileImageLastAction) => {
    if (mobileImageLastAction && !mobilePhotoUploadChoiceMade) {
      sendEvent("event", "interaction", {
        label: `${mobileImageLastAction === MobilePhotoAction.TAKE_PHOTO ? "Take Photo" : "Photo library"}`
      });
      dispatch(imageUploadSlice.actions.setMobilePhotoUploadChoiceMade(true));
    }
    dispatch(
      imageUploadSlice.actions.setMobileImageLastAction(mobileImageLastAction)
    );
    const validationWarning = getIsInValid(imageFiles, maxUploads);
    if (!validationWarning) {
      const images = formatImagesForPost(currentHelper, imageFiles);
      handleChange(images);
      dispatch(
        imageUploadSlice.actions.setMaxUploads(maxUploads - imageFiles.length)
      );
    } else {
      setValidation(validationWarning);
    }
  };

  return (
    <PageLayout>
      <PageLayout.Header
        title="Photograph vehicle"
        step={isCompactView ? 6 : 12}
      />
      <PageLayout.Content>
        <>
          <p
            className={`ch-fs--4 ${showPreviewImage ? "" : "sm:ch-mb--5"}`}
            aria-label={label}
          >
            {helperTextAboveImage}
          </p>
          {showPreviewImage && (
            <div className="tip-card ch-bg--white ch-ba--1 ch-bc--grey-3">
              <span className="tip-card__example-flag ch-color--white ch-ph--1">
                EXAMPLE
              </span>
              <img
                src={image}
                className="tip-card__example-img"
                alt="example-upload"
                onError={(e) => tryAgain(e.target)}
              />
            </div>
          )}
          <div
            className={`ch-bg--grey-1 ch-br--1 ch-bl--1 ch-bb--1 ch-bc--grey-3 ch-pa--3 ch-mb--4 ch-shadow--sm ${
              showPreviewImage ? "" : "ch-bt--1"
            }`}
          >
            <p className={`ch-fs--4 ${showPreviewImage ? "" : "ch-mb--0"}`}>
              Upload {helperTextBelowImage}
            </p>
          </div>
        </>
        {validation && (
          <div className="ch-form__group ch-form__group--error">
            <span className="ch-form__control-validation ">{validation}</span>
          </div>
        )}
      </PageLayout.Content>
      <PageLayout.Footer>
        {hasAllRequiredImagesUploaded ? (
          <button
            type="button"
            className="ch-btn ch-btn--success ch-display--block ch-mh--auto ch-width--12 sl:ch-width--8 sm:ch-width--5 md:ch-width--4"
            onClick={() => navigate("/photo-gallery")}
          >
            Continue
          </button>
        ) : mobileDeviceWithCamera ? (
          <MobilePhotoActions
            imageQuantityRequired={imageQuantityRequired}
            onChange={onChange}
            uploadMore={false}
            labelIdentifier={"mobile-photo-action"}
            disable={false}
          />
        ) : (
          <FilePicker
            buttonLabel={`Upload photo${imageQuantityRequired > 1 ? "s" : ""}`}
            buttonClass="ch-btn--success ch-btn--block ch-mh--auto ch-width--12 sl:ch-width--8 sm:ch-width--5 md:ch-width--4"
            handleChange={(images) => onChange(images)}
          />
        )}
      </PageLayout.Footer>
    </PageLayout>
  );
}

Helper.propTypes = {
  currentHelper: PropTypes.shape({
    label: PropTypes.string,
    helperText: PropTypes.string,
    boldLabelText: PropTypes.string,
    boldHeaderText: PropTypes.string,
    imageQuantityRequired: PropTypes.number,
    boldHeaderTextAdditional: PropTypes.string,
    image: PropTypes.string
  }).isRequired,
  handleChange: PropTypes.func.isRequired
};
